<template>
    <div class="h-120 w-80 sm:w-full">
        <div
            ref="editorContainer"
            class="border border-gray-400 rounded-md h-full overflow-hidden"
        ></div>
    </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, onUnmounted, ref, watch } from "vue";

export default defineComponent({
    props: {
        readOnly: {
            type: Boolean,
            default: () => false,
            required: false
        },
        value: {
            type: String,
            default: () => "",
            required: true
        },
        dark: {
            type: Boolean,
            default: false
        },
        language: {
            type: String,
            default: () => "json",
            required: false
        }
    },
    setup(props, { emit }) {
        const editorContainer = ref<HTMLElement | null>(null);
        let editor: any = null;

        const initEditor = async () => {
            if (editorContainer.value) {
                const monaco = await import("monaco-editor");

                editor = monaco.editor.create(editorContainer.value, {
                    value: props.value,
                    language: props.language,
                    theme: props.dark ? "vs-dark" : "vs-light",
                    readOnly: props.readOnly,
                    automaticLayout: true,
                    tabSize: 4,
                    lineNumbers: "on",
                    minimap: {
                        enabled: false
                    },
                    scrollBeyondLastLine: false,
                    scrollbar: {
                        vertical: "visible",
                        horizontal: "visible",
                        useShadows: false,
                        verticalScrollbarSize: 10,
                        horizontalScrollbarSize: 10,
                        alwaysConsumeMouseWheel: false
                    },
                    mouseWheelZoom: false,
                    smoothScrolling: true
                });

                editor.onDidChangeModelContent(() => {
                    const value = editor?.getValue();
                    emit("update:value", value);
                });
            }
        };

        onMounted(() => {
            initEditor();
        });

        onUnmounted(() => {
            if (editor) {
                editor.dispose();
            }
            document.body.style.overflow = "auto";
        });

        watch(
            () => props.value,
            (newValue) => {
                if (editor && newValue !== editor.getValue()) {
                    editor.setValue(newValue);
                }
            }
        );

        watch(
            () => props.dark,
            (newValue) => {
                if (editor) {
                    editor.updateOptions({
                        theme: newValue ? "vs-dark" : "vs-light"
                    });
                }
            }
        );

        return {
            editorContainer
        };
    }
});
</script>
